import React, { useState, useEffect, useCallback } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { 
    ButtonGroup, 
    Button, 
    Tooltip, 
    Menu, 
    MenuItem, 
    Divider, 
    Typography,
    Box,
    Collapse,
    List,
    ListItem,
    ListItemText,
    Paper
} from '@material-ui/core';
import { 
    Map as MapIcon, 
    Satellite, 
    Terrain, 
    ExpandMore, 
    ExpandLess,
    History
} from '@material-ui/icons';

const useStyles = makeStyles(theme => ({
    providerControl: {
        position: 'absolute',
        bottom: 20,
        right: 20,
        zIndex: 1000,
        backgroundColor: 'white',
        borderRadius: theme.shape.borderRadius,
        boxShadow: theme.shadows[3],
        overflow: 'visible',
        display: 'flex',
        flexDirection: 'column'
    },
    mainButtons: {
        display: 'flex',
        borderRadius: 0,
        '& .MuiButton-root': {
            borderRadius: 0,
            padding: theme.spacing(1),
            minWidth: 40,
            backgroundColor: 'white'
        }
    },
    providerButton: {
        textTransform: 'none',
        justifyContent: 'flex-start',
        padding: theme.spacing(1, 2),
        width: '100%'
    },
    groupLabel: {
        padding: theme.spacing(1, 2),
        backgroundColor: theme.palette.grey[100],
        fontWeight: 'bold',
        fontSize: '0.75rem',
        color: theme.palette.text.secondary
    },
    selected: {
        backgroundColor: theme.palette.primary.light,
        color: theme.palette.primary.contrastText,
        '&:hover': {
            backgroundColor: theme.palette.primary.main,
        }
    },
    moreButton: {
        borderTop: `1px solid ${theme.palette.divider}`,
        borderRadius: 0,
        width: '100%',
        display: 'flex',
        justifyContent: 'space-between',
        padding: theme.spacing(0.5, 2),
        backgroundColor: theme.palette.background.paper
    },
    yearsList: {
        position: 'absolute',
        bottom: '100%',
        right: 0,
        marginBottom: theme.spacing(1),
        maxHeight: '300px',
        width: '200px',
        overflowY: 'auto',
        backgroundColor: 'white',
        boxShadow: theme.shadows[3],
        borderRadius: theme.shape.borderRadius
    },
    yearItem: {
        padding: theme.spacing(1, 2)
    },
    selectedYear: {
        backgroundColor: theme.palette.action.selected
    }
}));

const MapProviderSelector = ({ 
    providers, 
    selectedProvider, 
    onProviderChange 
}) => {
    const classes = useStyles();
    const [anchorEl, setAnchorEl] = useState(null);
    const [groupedProviders, setGroupedProviders] = useState({});
    const [showMaricopaYears, setShowMaricopaYears] = useState(false);
    
    // Group providers by type
    useEffect(() => {
        const grouped = providers.reduce((acc, provider) => {
            const group = provider.group || 'other';
            if (!acc[group]) acc[group] = [];
            acc[group].push(provider);
            return acc;
        }, {});
        
        // Ensure Google is first if it exists
        if (grouped.google) {
            const orderedGroups = { google: grouped.google };
            Object.keys(grouped).forEach(key => {
                if (key !== 'google') orderedGroups[key] = grouped[key];
            });
            setGroupedProviders(orderedGroups);
        } else {
            setGroupedProviders(grouped);
        }
    }, [providers]);
    
    // Handle opening more providers menu
    const handleOpenMenu = useCallback((event) => {
        setAnchorEl(event.currentTarget);
    }, []);
    
    // Handle closing more providers menu
    const handleCloseMenu = useCallback(() => {
        setAnchorEl(null);
    }, []);
    
    // Handle selecting a provider
    const handleSelectProvider = useCallback((provider) => {
        onProviderChange(provider);
        handleCloseMenu();
    }, [onProviderChange, handleCloseMenu]);
    
    // Toggle Maricopa years dropdown
    const toggleMaricopaYears = useCallback((e) => {
        e.stopPropagation();
        setShowMaricopaYears(prev => !prev);
    }, []);
    
    // Get icon for provider
    const getProviderIcon = useCallback((provider) => {
        const name = provider.name.toLowerCase();
        
        if (name.includes('satellite')) {
            return <Satellite fontSize="small" />;
        } else if (name.includes('terrain') || name.includes('hybrid')) {
            return <Terrain fontSize="small" />;
        } else if (name.includes('maricopa')) {
            return <History fontSize="small" />;
        } else {
            return <MapIcon fontSize="small" />;
        }
    }, []);

    // Check if we have Maricopa providers
    const hasMaricopaProviders = Boolean(groupedProviders.maricopa?.length);

    // Quick buttons for common providers (Google Maps variants)
    const quickButtons = providers.filter(p => p.group === 'google').slice(0, 3);

    // Get Maricopa providers sorted by year descending
    const maricopaProviders = (groupedProviders.maricopa || [])
        .sort((a, b) => b.year - a.year);

    return (
        <div className={classes.providerControl}>
            {/* Main quick access buttons */}
            <ButtonGroup variant="contained" className={classes.mainButtons}>
                {quickButtons.map(provider => (
                    <Tooltip key={provider.name} title={provider.displayName || provider.name}>
                        <Button 
                            onClick={() => handleSelectProvider(provider)}
                            className={selectedProvider.name === provider.name ? classes.selected : ''}
                        >
                            {getProviderIcon(provider)}
                        </Button>
                    </Tooltip>
                ))}
                
                {/* Maricopa toggle button if we have Maricopa providers */}
                {hasMaricopaProviders && (
                    <Tooltip title="Maricopa County Aerial Imagery">
                        <Button onClick={toggleMaricopaYears}>
                            <History />
                            {showMaricopaYears ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                        </Button>
                    </Tooltip>
                )}
                
                {/* More providers button */}
                {/* <Button onClick={handleOpenMenu}>
                    <ExpandMore />
                </Button> */}
            </ButtonGroup>
            
            {/* Maricopa years dropdown */}
            {hasMaricopaProviders && (
                <Collapse in={showMaricopaYears}>
                    <Paper className={classes.yearsList}>
                        <List component="div" dense>
                            <Typography className={classes.groupLabel}>
                                MARICOPA COUNTY AERIALS
                            </Typography>
                            {maricopaProviders.map(provider => (
                                <ListItem 
                                    key={provider.year} 
                                    button
                                    selected={selectedProvider.name === provider.name}
                                    onClick={() => handleSelectProvider(provider)}
                                    className={selectedProvider.name === provider.name ? classes.selectedYear : ''}
                                >
                                    <ListItemText primary={`${provider.year} Aerial`} />
                                </ListItem>
                            ))}
                        </List>
                    </Paper>
                </Collapse>
            )}
            
            {/* Full provider menu */}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleCloseMenu}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
            >
                {Object.entries(groupedProviders).map(([groupName, groupProviders]) => (
                    <div key={groupName}>
                        <Typography className={classes.groupLabel}>
                            {groupName.toUpperCase()}
                        </Typography>
                        {groupProviders.map(provider => (
                            <MenuItem 
                                key={provider.name}
                                onClick={() => handleSelectProvider(provider)}
                                selected={selectedProvider.name === provider.name}
                                className={selectedProvider.name === provider.name ? classes.selected : ''}
                            >
                                <Box display="flex" alignItems="center">
                                    <Box mr={1}>{getProviderIcon(provider)}</Box>
                                    {provider.displayName || provider.name}
                                </Box>
                            </MenuItem>
                        ))}
                        <Divider />
                    </div>
                ))}
            </Menu>
        </div>
    );
};

export default MapProviderSelector;